import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { SignpostModal } from './SignpostModal';
import { UpdateOperation } from './types';
import { useSignpostData } from './useData';

interface Item {
  id: number;
  condition: string;
  note: string;
  aware: string;
  signpost: string;
  appointment: string;
  signpost_2: string;
  appointment_2: string;
  information_source: string;
  information_url: string;
  deleted: boolean;
}

const Signpost: React.FC<{ isAdmin: boolean }> = ({ isAdmin }: { isAdmin: boolean }) => {
  const { retrieveSignpostData, saveSignpostData, dataVersion } = useSignpostData();
  const [signpostData, updateSignpostData] = useState<Item[]>();
  const [showDeleted, updateShowDeleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [selectedItem, updateSelectedItem] = useState<any>({});
  const [showModal, setShowModal] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    retrieveSignpostData()
      .then((signpostData: any) => {
        setLoadingError(null);
        updateSignpostData(signpostData);
      })
      .catch((error: any) => {
        const message = 'Signpost data could not be loaded.';
        setLoadingError(message);
        updateSignpostData([]);
      })
      .finally(() => setIsLoading(false));
  }, [dataVersion, retrieveSignpostData]);

  useEffect(() => {
    if (signpostData) {
      const fuse = new Fuse(
        showDeleted && isAdmin ? signpostData : signpostData.filter((item) => !item.deleted),
        {
          keys: ['condition', 'note', 'aware', 'signpost', 'signpost_2'],
          threshold: 0.3, // Adjust the threshold for fuzzy matching
        },
      );
      if (isAdmin && !searchTerm) {
        setResults(showDeleted ? signpostData : signpostData.filter((item) => !item.deleted));
      } else setResults(fuse.search(searchTerm).map((result) => result.item));
    }
  }, [signpostData, searchTerm, showDeleted, isAdmin]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleEditItemClick = (id: number) => {
    updateSelectedItem(results.find((x) => x.id === id));
    setShowModal(true);
  };

  const handleNewItemClick = () => {
    const maxId = signpostData?.reduce((previous, current) =>
      previous.id > current.id ? previous : current,
    ).id;
    if (maxId) {
      updateSelectedItem({ id: maxId + 1 });
      setShowModal(true);
    }
  };

  const handleModalClose = ({
    updateOperation,
    itemToUpdate,
    isNew,
  }: {
    updateOperation: UpdateOperation;
    itemToUpdate: any;
    isNew: boolean;
  }) => {
    if (signpostData) {
      let newData;
      if (updateOperation === UpdateOperation.SAVE) {
        if (isNew) {
          signpostData.push(itemToUpdate);
          newData = signpostData;
        } else {
          newData = signpostData.map((item) => (item.id !== itemToUpdate.id ? item : itemToUpdate));
        }
        saveSignpostData(newData);
      } else if (updateOperation === UpdateOperation.DELETE) {
        const newData = signpostData.map((item) =>
          item.id === itemToUpdate.id ? { ...item, deleted: !item.deleted } : item,
        );
        saveSignpostData(newData);
      }
    }
    setShowModal(false);
  };

  const handleShowDeleted = () => updateShowDeleted(!showDeleted);

  return (
    <Form>
      {loadingError && (
        <Alert variant="danger" data-testid="signpostLoadError" role="alert">
          {loadingError}
        </Alert>
      )}
      <Row>
        <Col>
          {isAdmin && (
            <Form.Check
              data-testid="showDeleted"
              type="switch"
              label="Show Deleted Items"
              reverse
              onChange={handleShowDeleted}
            ></Form.Check>
          )}
        </Col>
        <Col>
          {isLoading ? (
            <Spinner />
          ) : (
            signpostData &&
            signpostData.length > 0 && (
              <>
                <Form.Control
                  data-testid="searchInput"
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  id="search"
                  aria-describedby="search"
                  onChange={handleSearch}
                />
                {!searchTerm && (
                  <Form.Text id="search">Start typing a keyword for your enquiry</Form.Text>
                )}
                <br />
              </>
            )
          )}
        </Col>
        <Col>{isAdmin && <Button onClick={handleNewItemClick}>Add new condition</Button>}</Col>
      </Row>

      {(searchTerm || isAdmin) && (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Condition</th>
              <th>Note</th>
              <th>Be Aware</th>
              <th>Signpost</th>
              <th>Appt Length</th>
              <th>2nd Level Signpost</th>
              <th>Appt Length</th>
              <th>Information</th>
            </tr>
          </thead>
          <tbody>
            {results.map(
              ({
                id,
                condition,
                note,
                aware,
                signpost,
                appointment,
                signpost_2,
                appointment_2,
                information_source,
                information_url,
              }) => {
                return (
                  <tr key={id} onClick={isAdmin ? () => handleEditItemClick(id) : () => {}}>
                    <td>{condition}</td>
                    <td>{note}</td>
                    <td>{aware}</td>
                    <td>{signpost}</td>
                    <td>{appointment}</td>
                    <td>{signpost_2}</td>
                    <td>{appointment_2}</td>
                    <td>
                      {information_url && (
                        <a href={information_url} target="_blank" rel="noreferrer">
                          {information_source}
                        </a>
                      )}
                    </td>
                  </tr>
                );
              },
            )}
          </tbody>
        </Table>
      )}
      <SignpostModal
        showModal={showModal}
        handleModalClose={handleModalClose}
        selectedItem={selectedItem}
      />
    </Form>
  );
};

export default Signpost;
